import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import {
    Shield, Users, Building2, Clock, TrendingUp, AlertTriangle,
    Power, Calendar, Trash2, ChevronDown, RefreshCw, Search,
    Crown, Zap, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const PLAN_COLORS = {
    trial: { bg: 'bg-amber-500/10', text: 'text-amber-500', border: 'border-amber-500/30', label: 'Trial' },
    basico: { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/30', label: 'Básico' },
    premium: { bg: 'bg-emerald-500/10', text: 'text-emerald-500', border: 'border-emerald-500/30', label: 'Premium' },
};

function StatCard({ icon: Icon, label, value, sub, color }) {
    return (
        <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`} className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
                <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center`}>
                    <Icon className="w-4.5 h-4.5" />
                </div>
                <span className="text-sm text-muted-foreground">{label}</span>
            </div>
            <p className="text-2xl font-bold">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
    );
}

function PlanBadge({ plan }) {
    const p = PLAN_COLORS[plan] || PLAN_COLORS.trial;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${p.bg} ${p.text} border ${p.border}`}>
            {plan === 'premium' && <Crown className="w-3 h-3" />}
            {plan === 'basico' && <Zap className="w-3 h-3" />}
            {plan === 'trial' && <Clock className="w-3 h-3" />}
            {p.label}
        </span>
    );
}

export default function SuperAdminPage() {
    const [stats, setStats] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [expandedId, setExpandedId] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsRes, tenantsRes] = await Promise.all([
                api.get('/superadmin/stats'),
                api.get('/superadmin/tenants'),
            ]);
            setStats(statsRes.data);
            setTenants(tenantsRes.data);
        } catch (err) {
            toast.error('Error cargando datos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleChangePlan = async (tenantId, plan) => {
        setActionLoading(`plan-${tenantId}`);
        try {
            const res = await api.put(`/superadmin/tenants/${tenantId}/plan?plan=${plan}`);
            toast.success(res.data.message);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error');
        } finally {
            setActionLoading(null);
        }
    };

    const handleToggle = async (tenantId) => {
        setActionLoading(`toggle-${tenantId}`);
        try {
            const res = await api.put(`/superadmin/tenants/${tenantId}/toggle`);
            toast.success(res.data.message);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error');
        } finally {
            setActionLoading(null);
        }
    };

    const handleExtendTrial = async (tenantId, dias = 15) => {
        setActionLoading(`extend-${tenantId}`);
        try {
            const res = await api.put(`/superadmin/tenants/${tenantId}/extend-trial?dias=${dias}`);
            toast.success(res.data.message);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDelete = async (tenantId, name) => {
        if (!window.confirm(`ELIMINAR "${name}" y TODOS sus datos? Esta acción NO se puede deshacer.`)) return;
        setActionLoading(`delete-${tenantId}`);
        try {
            const res = await api.delete(`/superadmin/tenants/${tenantId}`);
            toast.success(res.data.message);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error');
        } finally {
            setActionLoading(null);
        }
    };

    const filtered = tenants.filter(t =>
        t.nombre_empresa.toLowerCase().includes(search.toLowerCase()) ||
        t.email.toLowerCase().includes(search.toLowerCase())
    );

    if (loading && !stats) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
            </div>
        );
    }

    return (
        <div data-testid="superadmin-page" className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Panel Super Admin</h1>
                        <p className="text-sm text-muted-foreground">Gestión de empresas y planes</p>
                    </div>
                </div>
                <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
                    <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                    Actualizar
                </Button>
            </div>

            {/* Stats Grid */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <StatCard icon={Building2} label="Total Empresas" value={stats.total_tenants} color="bg-primary/10 text-primary" />
                    <StatCard icon={Power} label="Activos" value={stats.tenants_activos} sub={`${stats.tenants_expirados} expirados`} color="bg-emerald-500/10 text-emerald-500" />
                    <StatCard icon={Clock} label="En Trial" value={stats.tenants_trial} sub={`${stats.nuevos_7d} nuevos (7d)`} color="bg-amber-500/10 text-amber-500" />
                    <StatCard icon={Users} label="Total Usuarios" value={stats.total_users} color="bg-blue-500/10 text-blue-500" />
                    <StatCard icon={TrendingUp} label="Total Préstamos" value={stats.total_prestamos} sub={`${stats.total_clientes} clientes`} color="bg-violet-500/10 text-violet-500" />
                </div>
            )}

            {/* Tenants Table */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="p-4 border-b border-border flex items-center justify-between gap-4">
                    <h2 className="font-semibold text-lg">Empresas Registradas</h2>
                    <div className="relative w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            data-testid="search-tenants"
                            placeholder="Buscar empresa..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="pl-9 h-9"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border bg-muted/30">
                                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Empresa</th>
                                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Plan</th>
                                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Estado</th>
                                <th className="text-center px-4 py-3 font-medium text-muted-foreground">Usuarios</th>
                                <th className="text-center px-4 py-3 font-medium text-muted-foreground">Clientes</th>
                                <th className="text-center px-4 py-3 font-medium text-muted-foreground">Préstamos</th>
                                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Trial</th>
                                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(t => (
                                <React.Fragment key={t.id}>
                                    <tr
                                        data-testid={`tenant-row-${t.id}`}
                                        className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${!t.activo ? 'opacity-60' : ''}`}
                                    >
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{t.nombre_empresa}</div>
                                            <div className="text-xs text-muted-foreground">{t.email}</div>
                                        </td>
                                        <td className="px-4 py-3"><PlanBadge plan={t.plan} /></td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center gap-1 text-xs font-medium ${t.activo ? 'text-emerald-500' : 'text-red-500'}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${t.activo ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                                {t.activo ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">{t.stats.usuarios}</td>
                                        <td className="px-4 py-3 text-center">{t.stats.clientes}</td>
                                        <td className="px-4 py-3 text-center">{t.stats.prestamos}</td>
                                        <td className="px-4 py-3">
                                            {t.dias_restantes !== null ? (
                                                <span className={`text-xs font-medium ${t.dias_restantes <= 3 ? 'text-red-500' : t.dias_restantes <= 7 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                                                    {t.dias_restantes}d restantes
                                                </span>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">N/A</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                                                data-testid={`expand-${t.id}`}
                                            >
                                                <Eye className="w-4 h-4 mr-1" />
                                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandedId === t.id ? 'rotate-180' : ''}`} />
                                            </Button>
                                        </td>
                                    </tr>
                                    {expandedId === t.id && (
                                        <tr data-testid={`tenant-actions-${t.id}`}>
                                            <td colSpan={8} className="px-4 py-4 bg-muted/20">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="text-xs text-muted-foreground mr-2 font-medium">Plan:</span>
                                                    {['trial', 'basico', 'premium'].map(plan => (
                                                        <Button
                                                            key={plan}
                                                            variant={t.plan === plan ? 'default' : 'outline'}
                                                            size="sm"
                                                            disabled={actionLoading === `plan-${t.id}` || t.plan === plan}
                                                            onClick={() => handleChangePlan(t.id, plan)}
                                                            data-testid={`set-plan-${plan}-${t.id}`}
                                                            className="h-7 text-xs"
                                                        >
                                                            {plan === 'trial' && <Clock className="w-3 h-3 mr-1" />}
                                                            {plan === 'basico' && <Zap className="w-3 h-3 mr-1" />}
                                                            {plan === 'premium' && <Crown className="w-3 h-3 mr-1" />}
                                                            {plan.charAt(0).toUpperCase() + plan.slice(1)}
                                                        </Button>
                                                    ))}

                                                    <div className="w-px h-6 bg-border mx-2" />

                                                    <Button
                                                        variant={t.activo ? 'destructive' : 'default'}
                                                        size="sm"
                                                        onClick={() => handleToggle(t.id)}
                                                        disabled={actionLoading === `toggle-${t.id}`}
                                                        data-testid={`toggle-${t.id}`}
                                                        className="h-7 text-xs"
                                                    >
                                                        <Power className="w-3 h-3 mr-1" />
                                                        {t.activo ? 'Desactivar' : 'Activar'}
                                                    </Button>

                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleExtendTrial(t.id, 15)}
                                                        disabled={actionLoading === `extend-${t.id}`}
                                                        data-testid={`extend-${t.id}`}
                                                        className="h-7 text-xs"
                                                    >
                                                        <Calendar className="w-3 h-3 mr-1" />
                                                        +15 días
                                                    </Button>

                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleExtendTrial(t.id, 30)}
                                                        disabled={actionLoading === `extend-${t.id}`}
                                                        className="h-7 text-xs"
                                                    >
                                                        <Calendar className="w-3 h-3 mr-1" />
                                                        +30 días
                                                    </Button>

                                                    <div className="w-px h-6 bg-border mx-2" />

                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDelete(t.id, t.nombre_empresa)}
                                                        disabled={actionLoading === `delete-${t.id}`}
                                                        data-testid={`delete-${t.id}`}
                                                        className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                                    >
                                                        <Trash2 className="w-3 h-3 mr-1" />
                                                        Eliminar
                                                    </Button>
                                                </div>
                                                <div className="mt-3 text-xs text-muted-foreground grid grid-cols-2 md:grid-cols-4 gap-2">
                                                    <div>Tel: {t.telefono || 'N/A'}</div>
                                                    <div>Registro: {new Date(t.created_at).toLocaleDateString('es-DO')}</div>
                                                    <div>Trial inicio: {t.trial_start ? new Date(t.trial_start).toLocaleDateString('es-DO') : 'N/A'}</div>
                                                    <div>Trial fin: {t.trial_end ? new Date(t.trial_end).toLocaleDateString('es-DO') : 'N/A'}</div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                                        {search ? 'No se encontraron empresas' : 'No hay empresas registradas'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
